import { Button } from "@dms/ui";
import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Глобальний error boundary (12-screen-spec §14, 500): ловить рендер-винятки
 * піддерева і показує fallback «Щось пішло не так» замість білого екрана.
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // У MVP лог у консоль; інтеграція з error-tracking — окрема фіча.
    console.error("UI error boundary", error, info.componentStack);
  }

  private handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    if (this.state.error !== null) {
      return (
        <div
          role="alert"
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 12,
            minHeight: "100vh",
            padding: 24,
            textAlign: "center",
            background: "var(--color-surface-default)",
          }}
        >
          <h2 style={{ margin: 0, fontSize: "var(--fs-h2)", fontWeight: "var(--fw-bold)" }}>
            Щось пішло не так
          </h2>
          <p style={{ margin: 0, maxWidth: 420, color: "var(--color-text-secondary)", fontSize: "var(--fs-body)" }}>
            Сталася непередбачена помилка. Спробуйте перезавантажити сторінку або зверніться до підтримки.
          </p>
          <Button onClick={this.handleReload}>Перезавантажити</Button>
        </div>
      );
    }
    return this.props.children;
  }
}
